import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import "../styles/EditUserModal.css";
import { getMarketSymbols, activateProduction, activateAccountManually, paySubscription, confirmSubscriptionPayment, confirmActivationPayment, markSubscriptionPaid } from "../services/api";
import TradingPairsSelector, { normalizeTradingPairs } from "./TradingPairsSelector";

const EditUserModal = ({
  open,
  user,
  onClose,
  onSave,
  loading,
  error,
  success,
  title = "Editar usuario",
  subtitle,
  submitLabel = "Guardar",
  includeIdentityFields = false,
  forceComplete = false,
  allowAdminControls = true,
  allowPromptControl = false,
  allowPasswordChange = false,
  preloadSensitiveFields = false,
  activationWallet: activationWalletProp = "",
  activationPionexRecipient: activationPionexRecipientProp = "",
  activationPionexUid: activationPionexUidProp = "",
  subscriptionInfo = null,
  onSubscriptionConfirmed = null,
}) => {
  const [fullName, setFullName] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [tradingPairs, setTradingPairs] = useState([]);
  const [tradeMode, setTradeMode] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [telegramChatIds, setTelegramChatIds] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState("");
  const [hideProfilePrompt, setHideProfilePrompt] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [activationHash, setActivationHash] = useState("");
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationStatus, setActivationStatus] = useState({ type: "", text: "" });
  const [activationQR, setActivationQR] = useState("");
  const [activationMethod, setActivationMethod] = useState("tron");
  const [activationOccurredAt, setActivationOccurredAt] = useState("");
  const [actAdminBusy, setActAdminBusy] = useState(false);
  const [actAdminStatus, setActAdminStatus] = useState({ type: "", text: "" });
  const [actManualBusy, setActManualBusy] = useState(false);
  const [actManualStatus, setActManualStatus] = useState({ type: "", text: "" });
  const [actManualNote, setActManualNote] = useState("");
  const [subscriptionPct, setSubscriptionPct] = useState("");
  const [subscriptionPeriodDays, setSubscriptionPeriodDays] = useState("");
  const [subscriptionPeriodType, setSubscriptionPeriodType] = useState("");
  const [subscriptionBillingDay, setSubscriptionBillingDay] = useState("");
  const [subHash, setSubHash] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [subStatus, setSubStatus] = useState({ type: "", text: "" });
  const [subQR, setSubQR] = useState("");
  const [subPaymentMethod, setSubPaymentMethod] = useState("tron");
  const [subOccurredAt, setSubOccurredAt] = useState("");
  const [subAdminBusy, setSubAdminBusy] = useState(false);
  const [subAdminStatus, setSubAdminStatus] = useState({ type: "", text: "" });
  const [subPaidAmount, setSubPaidAmount] = useState("");
  const [subPaidRef, setSubPaidRef] = useState("");

  const originalRef = useRef({ telegramChatIds: "", fullName: "", mobilePhone: "" });
  const keysTouchedRef = useRef({ key: false, secret: false });
  const subAmountPrefilledRef = useRef(false);

  const activationWallet = useMemo(
    () => String(activationWalletProp || "").trim(),
    [activationWalletProp]
  );
  const pionexUid = useMemo(
    () => String(activationPionexUidProp || "").trim(),
    [activationPionexUidProp]
  );

  useEffect(() => {
    if (!activationWallet) return;
    let cancelled = false;
    const tronLink = `tron://transfer?contract=TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t&address=${activationWallet}&amount=250&tokenSymbol=USDT`;
    QRCode.toDataURL(tronLink, { width: 200, margin: 1, errorCorrectionLevel: "M" })
      .then((url) => {
        if (!cancelled) setActivationQR(url);
      })
      .catch(() => {
        if (!cancelled) setActivationQR("");
      });
    return () => {
      cancelled = true;
    };
  }, [activationWallet]);

  const subWallet = useMemo(
    () => String(subscriptionInfo?.wallet || activationWallet || "").trim(),
    [subscriptionInfo, activationWallet]
  );
  const subDueAmount = useMemo(
    () => {
      const value = Number(subscriptionInfo?.due_amount);
      return Number.isFinite(value) && value > 0 ? value : 0;
    },
    [subscriptionInfo]
  );
  const subAutoAmount = useMemo(
    () => {
      const pendingLive = Number(user?.sub?.payment_pending_amount);
      if (Number.isFinite(pendingLive) && pendingLive > 0) return pendingLive;
      const pendingLegacy = Number(user?.subscription_pending_amount);
      if (Number.isFinite(pendingLegacy) && pendingLegacy > 0) return pendingLegacy;
      const dueLive = Number(user?.sub?.due_amount);
      if (Number.isFinite(dueLive) && dueLive > 0) return dueLive;
      const dueLegacy = Number(user?.subscription_due_amount);
      if (Number.isFinite(dueLegacy) && dueLegacy > 0) return dueLegacy;
      return 0;
    },
    [user]
  );
  const showSubPayBlock = useMemo(
    () =>
      String(tradeMode || "").trim().toUpperCase() === "REAL" && Boolean(subscriptionInfo?.active),
    [tradeMode, subscriptionInfo]
  );

  useEffect(() => {
    if (!showSubPayBlock || !subWallet) {
      setSubQR("");
      return;
    }
    let cancelled = false;
    const amountParam = subDueAmount > 0 ? `&amount=${subDueAmount}` : "";
    const tronLink = `tron://transfer?contract=TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t&address=${subWallet}${amountParam}&tokenSymbol=USDT`;
    QRCode.toDataURL(tronLink, { width: 180, margin: 1, errorCorrectionLevel: "M" })
      .then((url) => {
        if (!cancelled) setSubQR(url);
      })
      .catch(() => {
        if (!cancelled) setSubQR("");
      });
    return () => {
      cancelled = true;
    };
  }, [showSubPayBlock, subWallet, subDueAmount, tradeMode]);

  const userIdKey = user?.id || user?.username || "";

  useEffect(() => {
    if (open) {
      setFullName(user?.full_name || "");
      setMobilePhone(user?.mobile_phone || "");
      setTradingPairs(normalizeTradingPairs(user?.trading_pairs || [], user?.symbol || ""));
      setTradeMode((user?.trade_mode || "").toString().toUpperCase());
      setApiKey(preloadSensitiveFields ? user?.pionex_api_key || "" : "");
      setApiSecret(preloadSensitiveFields ? user?.pionex_api_secret || "" : "");
      keysTouchedRef.current = { key: false, secret: false };
      const rawChatIds = Array.isArray(user?.telegram_chat_ids)
        ? user.telegram_chat_ids.join(", ")
        : user?.telegram_chat_ids || "";
      setTelegramChatIds(rawChatIds);
      // Guarda los valores originales para solo enviar campos realmente modificados
      // (evita que al editar un campo se sobrescriban/borren los demás).
      originalRef.current = {
        telegramChatIds: rawChatIds,
        fullName: user?.full_name || "",
        mobilePhone: user?.mobile_phone || "",
      };
      setShowSecret(false);
      setHideProfilePrompt(Boolean(user?.profile_prompt_hidden));
      setNewPassword("");
      setConfirmPassword("");
      setSubscriptionPct(user?.subscription_pct != null ? String(user.subscription_pct) : "");
      setSubscriptionPeriodDays(user?.subscription_period_days != null ? String(user.subscription_period_days) : "");
      setSubscriptionPeriodType(user?.subscription_period_type || "");
      setSubscriptionBillingDay(user?.subscription_billing_day != null ? String(user.subscription_billing_day) : "");
      setActivationMethod("tron");
      setActivationHash("");
      setActivationOccurredAt("");
      setSubHash("");
      setSubLoading(false);
      setSubStatus({ type: "", text: "" });
      setSubPaymentMethod("tron");
      setSubOccurredAt("");
      setSubAdminBusy(false);
      setSubAdminStatus({ type: "", text: "" });
      subAmountPrefilledRef.current = false;
    }
  }, [open, userIdKey, preloadSensitiveFields]);

  useEffect(() => {
    if (!open || subAmountPrefilledRef.current) return;
    const isReal = String(tradeMode || "").trim().toUpperCase() === "REAL";
    const hasPact = isReal && Number(user?.subscription_pct) > 0;
    if (hasPact) {
      subAmountPrefilledRef.current = true;
      setSubPaidAmount(Number(subAutoAmount).toFixed(2));
    }
  }, [open, subAutoAmount, tradeMode, user]);

  const hasSymbolOptions = useMemo(() => {
    if (Array.isArray(symbolOptions)) return symbolOptions.length > 0;
    return Boolean(symbolOptions?.spot_symbols?.length || symbolOptions?.futures_symbols?.length || symbolOptions?.symbols?.length);
  }, [symbolOptions]);

  const loadSymbols = useCallback(async (force = false) => {
    if (symbolsLoading) return;
    if (!force && hasSymbolOptions) return;
    setSymbolsLoading(true);
    setSymbolsError("");
    const result = await getMarketSymbols();
    if (!result.success) {
      setSymbolsError(result.error || "No se pudo cargar los pares");
      setSymbolOptions([]);
    } else {
      setSymbolOptions(result.data || {});
    }
    setSymbolsLoading(false);
  }, [hasSymbolOptions, symbolsLoading]);

  useEffect(() => {
    if (open) {
      void loadSymbols(false);
    }
  }, [open, loadSymbols]);

  const normalizedPairs = useMemo(() => normalizeTradingPairs(tradingPairs), [tradingPairs]);

  if (!open) return null;

  const effectiveDashboardView = "TRADE";
  const effectiveTradeMode = tradeMode || "SIM";
  const showTradeFields = true;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!user?.username) return;
    const normalizedSymbol = normalizedPairs[0]?.symbol || "";
    const normalizedTradeMode = String(tradeMode || "").trim().toUpperCase();
    const normalizedDashboardView = allowAdminControls ? "TRADE" : "";
    const normalizedApiKey = String(apiKey || "").trim();
    const normalizedApiSecret = String(apiSecret || "").trim();
    const originalKey = preloadSensitiveFields ? String(user?.pionex_api_key || "").trim() : null;
    const originalSecret = preloadSensitiveFields ? String(user?.pionex_api_secret || "").trim() : null;
    const keysTouched = keysTouchedRef.current || { key: false, secret: false };
    const keyChanged = keysTouched.key && normalizedApiKey !== originalKey;
    const secretChanged = keysTouched.secret && normalizedApiSecret !== originalSecret;
    const orig = originalRef.current || {};
    const telegramChanged = String(telegramChatIds || "").trim() !== String(orig.telegramChatIds || "").trim();
    const fullNameChanged = String(fullName || "").trim() !== String(orig.fullName || "").trim();
    const mobileChanged = String(mobilePhone || "").trim() !== String(orig.mobilePhone || "").trim();
    onSave?.({
      email: user.username,
      fullName: fullNameChanged ? fullName : null,
      mobilePhone: mobileChanged ? mobilePhone : null,
      symbol: normalizedSymbol,
      tradingPairs: normalizedPairs,
      tradeMode: allowAdminControls ? normalizedTradeMode || null : null,
      pionexApiKey: keysTouched.key && keyChanged ? normalizedApiKey || null : null,
      pionexApiSecret: keysTouched.secret && secretChanged ? normalizedApiSecret || null : null,
      telegramChatIds: allowAdminControls ? (telegramChanged ? telegramChatIds : null) : null,
      dashboardView: allowAdminControls ? normalizedDashboardView || null : null,
      hideProfilePrompt: allowPromptControl ? hideProfilePrompt : null,
      newPassword: allowPasswordChange ? newPassword : "",
      confirmPassword: allowPasswordChange ? confirmPassword : "",
      subscriptionPct: allowAdminControls ? (subscriptionPct === "" ? null : Number(subscriptionPct)) : null,
      subscriptionPeriodDays: allowAdminControls ? (subscriptionPeriodDays === "" ? null : Number(subscriptionPeriodDays)) : null,
      subscriptionPeriodType: allowAdminControls ? (subscriptionPeriodType === "" ? null : subscriptionPeriodType) : null,
      subscriptionBillingDay: allowAdminControls
        ? subscriptionBillingDay === ""
          ? null
          : Math.min(Math.max(Number(subscriptionBillingDay), 1), 31)
        : null,
    });
  };

  const handleSubPay = async () => {
    if (subLoading) return;
    setSubLoading(true);
    setSubStatus({ type: "", text: "" });
    const result =
      subPaymentMethod === "pionex"
        ? await paySubscription({ method: "pionex_internal", occurredAt: subOccurredAt.trim() })
        : await paySubscription({ txHash: subHash.trim(), method: "tron" });
    setSubLoading(false);
    if (result.success) {
      if (result.data?.status === "pending_review") {
        setSubStatus({
          type: "success",
          text: result.data?.message || "Pago registrado. En revisión del administrador.",
        });
        setSubOccurredAt("");
      } else {
        setSubStatus({
          type: "success",
          text: result.data?.message || "Pago validado. Suscripción al día.",
        });
        setSubHash("");
        setSubOccurredAt("");
      }
    } else {
      setSubStatus({ type: "error", text: result.error || "Error validando el pago." });
    }
  };

  return (
    <div className="edit-user-backdrop" onClick={() => { if (!forceComplete) onClose?.(); }}>
      <div className="edit-user-modal" onClick={(event) => event.stopPropagation()}>
        <header className="edit-user-header">
          <div>
            <h3>{title}</h3>
            <p>{subtitle || user?.username}</p>
          </div>
          {!forceComplete ? (
            <button
              type="button"
              className="edit-user-close"
              onClick={() => onClose?.()}
              aria-label="Cerrar"
            >
              ✕
            </button>
          ) : null}
        </header>

        {error ? <div className="edit-user-alert error">{error}</div> : null}
        {success ? <div className="edit-user-alert success">{success}</div> : null}
        {forceComplete ? (
          <div className="edit-user-alert warning">
              Completa tu configuración inicial para continuar. Si eliges Trade o Ambas, usa un símbolo real de la lista y vincula tu Telegram con <strong><a href="https://t.me/MultiTradeImBot" target="_blank" rel="noopener noreferrer">@MultiTradeImBot</a></strong> usando <strong>/start</strong>.
          </div>
        ) : null}

        <form className="edit-user-form" onSubmit={handleSubmit}>
          {includeIdentityFields ? (
            <>
              <label className="edit-user-field">
                <span>Nombre</span>
                <input
                  type="text"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  disabled={loading}
                />
              </label>

              <label className="edit-user-field">
                <span>Teléfono móvil</span>
                <input
                  type="text"
                  value={mobilePhone}
                  onChange={(event) => setMobilePhone(event.target.value)}
                  disabled={loading}
                />
              </label>
            </>
          ) : null}

          <div className="edit-user-assigned">
              <span className="edit-user-assigned-label">Vista asignada</span>
              <strong>Solo trade</strong>
            </div>

{effectiveTradeMode === "SIM" ? (
          <div className="edit-user-sim-pay">
            <p className="edit-user-sim-pay-text">
              Para activar el modo <strong>producción</strong> se cobra una activación de
              <strong>$250 USD</strong>. Completa las 3 zonas para activar tu cuenta.
            </p>

            <div className="edit-user-sim-pay-zone">
              <div className="edit-user-sim-pay-zone-title">
                <span className="edit-user-sim-pay-zone-num">1</span>
                Cómo hiciste el pago
              </div>
              <div className="edit-user-sub-methods">
                <button
                  type="button"
                  className={`edit-user-sub-method${activationMethod === "tron" ? " edit-user-sub-method--active" : ""}`}
                  onClick={() => setActivationMethod("tron")}
                >
                  <span className="edit-user-sub-method-title">Externo · otra wallet</span>
                  <span className="edit-user-sub-method-desc">
                    Pagas con una wallet TRC20 (TronLink, Trust, Binance…). Debes pegar el hash de la red.
                  </span>
                </button>
                <button
                  type="button"
                  className={`edit-user-sub-method${activationMethod === "pionex" ? " edit-user-sub-method--active" : ""}`}
                  onClick={() => setActivationMethod("pionex")}
                >
                  <span className="edit-user-sub-method-title">Pionex → Pionex · interno</span>
                  <span className="edit-user-sub-method-desc">
                    Pagas desde tu cuenta Pionex al UID del admin. Instantáneo, sin hash ni memo;
                    confirmas con el botón.
                  </span>
                </button>
              </div>
            </div>

            <div className="edit-user-sim-pay-zone">
              <div className="edit-user-sim-pay-zone-title">
                <span className="edit-user-sim-pay-zone-num">2</span>
                Pasos para pagar
              </div>

              {activationMethod === "tron" ? (
              <div className="edit-user-sim-pay-steps">
                <p>
                  <strong>Paso 1:</strong> envía <b>$250 USDT</b> (red TRC20) desde cualquier wallet a esta
                  dirección:
                </p>
                <code>{activationWallet || "Pendiente de definir"}</code>
                {activationQR ? (
                  <div className="edit-user-activate-qr">
                    <img src={activationQR} alt="QR pago USDT" width="180" height="180" />
                    <small>O escanea el QR con tu wallet (TronLink, Trust Wallet, etc.)</small>
                  </div>
                ) : null}
                <p>
                  <strong>Paso 2:</strong> cuando el pago esté confirmado, abre el historial de la transacción
                  y copia el <strong>TX ID (hash)</strong> — empieza con <code>T</code> (ej.{" "}
                  <code>TYs28Jr…</code>). Lo verás en tu wallet o buscando tu dirección en TronScan.
                </p>
              </div>
              ) : (
              <div className="edit-user-sim-pay-steps">
                <p>
                  <strong>Paso 1:</strong> dentro de tu cuenta Pionex abre{" "}
                  <b>Portafolio/Cartera → Transferir → Transferencia interna (Pionex a Pionex)</b> y envía{" "}
                  <b>$250 USDT</b> al UID del administrador:
                </p>
                <code className="edit-user-sim-pay-memo">{pionexUid || "UID_pendiente"}</code>
                {!pionexUid ? (
                  <p className="edit-user-sim-pay-warn">
                    El UID de la cuenta destino no está configurado aún. Escríbele al administrador antes de
                    transferir.
                  </p>
                ) : null}
                <p>
                  <strong>Paso 2:</strong> la transferencia llega <b>al instante</b> a la cuenta del
                  administrador. <b>No hay memo ni hash</b>; no escribas ningún código.
                </p>
                <p>
                  <strong>Paso 3 (recomendado):</strong> anota la <b>fecha y hora exacta</b> de la
                  transferencia tal como la muestra Pionex en tu historial (ej.{" "}
                  <code>21/09/2026 13:27:07</code>). Esa
                  misma hora la ve también el administrador y sirve para identificar tu pago.
                </p>
                <p>
                  <strong>Paso 4:</strong> pulsa <b>"Ya transferí · Verificar mi pago"</b> en la zona 3. Tu
                  cuenta quedará <b>en revisión</b> y el administrador la activará al confirmar el ingreso.
                </p>
              </div>
              )}
            </div>

            <div className="edit-user-sim-pay-zone">
              <div className="edit-user-sim-pay-zone-title">
                <span className="edit-user-sim-pay-zone-num">3</span>
                Ingresa tu comprobante
              </div>

              {activationMethod === "tron" ? (
              <div className="edit-user-activate-row">
                <input
                      type="text"
                      name="activation_tx_hash"
                      autoComplete="new-password"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      placeholder="Hash de la transacción (TX ID) — empieza con T"
                      value={activationHash}
                      onChange={(event) => setActivationHash(event.target.value)}
                      disabled={activationLoading}
                    />
                <button
                  type="button"
                  disabled={activationLoading || !activationHash.trim()}
                  onClick={async () => {
                    setActivationLoading(true);
                    setActivationStatus({ type: "", text: "" });
                    const result = await activateProduction({ txHash: activationHash.trim(), method: "tron" });
                    setActivationLoading(false);
                    if (result.success) {
                      setActivationStatus({
                        type: "success",
                        text: result.data?.message || "Pago validado. Cuenta activada en modo producción.",
                      });
                      if (result.data?.profile) {
                        const mode = String(result.data.profile.trade_mode || result.data.profile.mode || "").toUpperCase();
                        if (mode) setTradeMode(mode);
                      }
                    } else {
                      setActivationStatus({ type: "error", text: result.error || "Error validando el pago." });
                    }
                  }}
                >
                  {activationLoading ? "Validando..." : "Validar y activar producción"}
                </button>
              </div>
              ) : (
              <div className="edit-user-sim-pay-steps">
                <p>
                  Si tienes el historial abierto, pega aquí la <b>fecha y hora exacta</b> de la
                  transferencia tal como la muestra Pionex (ej.{" "}
                  <code>21/09/2026 13:27:07</code>). Ambos (tú y el administrador) ven la misma hora:
                  es tu comprobante.
                </p>
                <div className="edit-user-activate-row">
                  <input
                    type="text"
                    name="activation_pionex_occurred_at"
                    autoComplete="off"
                    autoCorrect="off"
                    placeholder="Fecha y hora exacta (ej. 21/09/2026 13:27:07)"
                    value={activationOccurredAt}
                    onChange={(event) => setActivationOccurredAt(event.target.value)}
                    disabled={activationLoading}
                  />
                  <button
                    type="button"
                    disabled={activationLoading}
                    onClick={async () => {
                      setActivationLoading(true);
                      setActivationStatus({ type: "", text: "" });
                      const result = await activateProduction({ method: "pionex", occurredAt: activationOccurredAt.trim() });
                      setActivationLoading(false);
                      if (result.success) {
                        setActivationStatus({
                          type: "success",
                          text: result.data?.message || "Aviso registrado. En revisión del administrador.",
                        });
                      } else {
                        setActivationStatus({ type: "error", text: result.error || "Error registrando el pago Pionex." });
                      }
                    }}
                  >
                    {activationLoading ? "Verificando..." : "Ya transferí · Verificar mi pago"}
                  </button>
                </div>
              </div>
              )}
              {activationStatus.text ? (
                <span className={`edit-user-activate-status edit-user-activate-status--${activationStatus.type}`}>
                  {activationStatus.text}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {allowAdminControls && effectiveTradeMode === "SIM" ? (
          <div className="edit-user-sub-admin-pending">
            <p>
              <strong>Activación manual (pago recibido fuera de línea)</strong>
            </p>
            <span>
              Si recibiste el pago <b>físicamente</b> (efectivo, transferencia bancaria, depósito
              manual, etc.) y no hay hash ni transferencia Pionex que registrar, activa la cuenta
              directamente. Queda trazado como <code>admin_manual</code>.
            </span>
            <div className="edit-user-activate-row">
              <input
                type="text"
                name="admin_activation_note"
                autoComplete="off"
                placeholder="Nota (ej. 'pagó en efectivo el 25-sep') — opcional"
                value={actManualNote}
                onChange={(event) => setActManualNote(event.target.value)}
                disabled={actManualBusy}
              />
              <button
                type="button"
                className="edit-user-primary"
                disabled={actManualBusy}
                onClick={async () => {
                  setActManualBusy(true);
                  setActManualStatus({ type: "", text: "" });
                  const result = await activateAccountManually(user.username, actManualNote.trim());
                  setActManualBusy(false);
                  if (result.success) {
                    setActManualStatus({ type: "success", text: result.data?.message || "Cuenta activada en modo producción." });
                    setActManualNote("");
                    if (result.data?.profile) {
                      const mode = String(result.data.profile.trade_mode || "").toUpperCase();
                      if (mode) setTradeMode(mode);
                    }
                  } else {
                    setActManualStatus({ type: "error", text: result.error || "Error activando manualmente." });
                  }
                }}
              >
                {actManualBusy ? "Activando..." : "Activar cuenta a producción (manual)"}
              </button>
            </div>
            {actManualStatus.text ? (
              <span className={`edit-user-activate-status edit-user-activate-status--${actManualStatus.type}`}>
                {actManualStatus.text}
              </span>
            ) : null}
          </div>
        ) : null}

        {allowAdminControls && user?.activation_pending ? (
          <div className="edit-user-sub-admin-pending">
            <p>
              <strong>Activación Pionex interna pendiente de verificar</strong>
            </p>
            <span>
              Este usuario declaró que transfirió <b>$250 USDT</b> por transferencia interna Pionex a tu UID.
              El pago no genera hash; verifícalo en tu app Pionex antes de confirmar.
            </span>
            <div className="edit-user-sub-admin-pending-data">
              <span>
                Monto a confirmar:{" "}
                {Number(user?.activation_pending_amount || 0) > 0
                  ? `${Number(user.activation_pending_amount).toFixed(2)} USDT`
                  : "—"}
              </span>
              {user?.activation_payment_occurred_at ? (
                <span>
                  Hora declarada de la transferencia: <code>{user.activation_payment_occurred_at}</code>
                </span>
              ) : null}
              {typeof user?.activation_spot_delta === "number" ? (
                <span
                  className={
                    user?.activation_spot_possible
                      ? "edit-user-sub-admin-hint edit-user-sub-admin-hint--ok"
                      : "edit-user-sub-admin-hint"
                  }
                >
                  {user?.activation_spot_possible ? (
                    <>
                      Posible ingreso detectado: el saldo spot del admin subió <b>+${Number(user.activation_spot_delta).toFixed(2)} USDT</b> desde el muestreo previo al aviso.
                    </>
                  ) : (
                    <>
                      Saldo spot del admin ahora: <b>{Number(user.activation_spot_now || 0).toFixed(2)}</b> USDT
                      (delta desde el muestreo previo: +{Number(user.activation_spot_delta).toFixed(2)}). El ingreso aún no parece reflejado.
                    </>
                  )}
                </span>
              ) : null}
              <span className="edit-user-sub-admin-pending-help">
                Revisa en tu app Pionex el historial de transferencias internas entrantes: busca una USDT por{" "}
                <b>
                  {Number(user?.activation_pending_amount || 0) > 0
                    ? `${Number(user.activation_pending_amount).toFixed(2)} USDT`
                    : "el monto"}
                </b>{" "}
                {user?.activation_payment_occurred_at ? (
                  <>a la hora <b>{user.activation_payment_occurred_at}</b></>
                ) : null}{" "}
                (la hora exacta es idéntica en ambos lados). Si coincide, confirma; si no, rechaza.
              </span>
            </div>
            <div className="edit-user-actions">
              <button
                type="button"
                className="edit-user-primary"
                disabled={actAdminBusy}
                onClick={async () => {
                  setActAdminBusy(true);
                  setActAdminStatus({ type: "", text: "" });
                  const result = await confirmActivationPayment(user.username, "confirm");
                  setActAdminBusy(false);
                  if (result.success) {
                    setActAdminStatus({ type: "success", text: result.data?.message || "Activación confirmada." });
                    if (result.data?.profile) {
                      const mode = String(result.data.profile.trade_mode || "").toUpperCase();
                      if (mode) setTradeMode(mode);
                    }
                  } else {
                    setActAdminStatus({ type: "error", text: result.error || "Error confirmando la activación." });
                  }
                }}
              >
                {actAdminBusy ? "Procesando..." : "Sí, el ingreso llegó · Confirmar"}
              </button>
              <button
                type="button"
                className="edit-user-secondary"
                disabled={actAdminBusy}
                onClick={async () => {
                  setActAdminBusy(true);
                  setActAdminStatus({ type: "", text: "" });
                  const result = await confirmActivationPayment(user.username, "reject");
                  setActAdminBusy(false);
                  if (result.success) {
                    setActAdminStatus({ type: "success", text: result.data?.message || "Activación rechazada." });
                  } else {
                    setActAdminStatus({ type: "error", text: result.error || "Error rechazando la activación." });
                  }
                }}
              >
                No llegó · Rechazar
              </button>
            </div>
            {actAdminStatus.text ? (
              <span className={`edit-user-sub-admin-status edit-user-sub-admin-status--${actAdminStatus.type}`}>
                {actAdminStatus.text}
              </span>
            ) : null}
          </div>
        ) : null}

        {showSubPayBlock ? (
          <div className="edit-user-sub-card">
            <div className="edit-user-sub-head">
              <strong>Tu suscripción</strong>
              <span>
                Pacto: <b>{subscriptionInfo?.pct != null ? `${subscriptionInfo.pct}%` : "—"}</b> sobre ganancias
                del período · Período de{" "}
                <b>
                  {subscriptionInfo?.period_type
                    ? (subscriptionInfo.period_type === "mensual"
                        ? "1 mes calendario"
                        : subscriptionInfo.period_type === "semanal"
                          ? "1 semana (7 días)"
                          : "2 semanas (quincenal)")
                    : subscriptionInfo?.period_days != null
                      ? `${subscriptionInfo.period_days} días`
                      : "—"}
                  {subscriptionInfo?.billing_day != null ? `, facturación el día ${subscriptionInfo.billing_day}` : ""}
                </b>
              </span>
            </div>

            {subscriptionInfo?.payment_pending ? (
              <div className="edit-user-sub-pending">
                <p>
                  Transferencia declarada y <strong>en revisión del administrador</strong>.
                </p>
                <span>
                  {subscriptionInfo.payment_occurred_at ? (
                    <>
                      Hora de la transferencia: <code>{subscriptionInfo.payment_occurred_at}</code> ·{" "}
                    </>
                  ) : null}
                  Monto:{" "}
                  {Number(subscriptionInfo.payment_pending_amount) > 0
                    ? `${Number(subscriptionInfo.payment_pending_amount).toFixed(2)} USDT`
                    : "—"}
                </span>
                <p >Tu bot se activará en cuanto el administrador confirme el pago.</p>
              </div>
            ) : null}

            {subscriptionInfo?.blocked ? (
              <div className="edit-user-sub-blocked">
                <p>
                  Tu suscripción venció y el bot quedó <strong>desactivado</strong>. Paga el monto adeudado para
                  reactivarlo.
                </p>
                <div className="edit-user-sub-amount">
                  Monto a pagar: <b>{subDueAmount > 0 ? `${subDueAmount.toFixed(2)} USDT` : "—"}</b>{" "}
                  <small>
                    (Externo: wallet <code>{subWallet || "Pendiente"}</code> · TRC20, o Pionex interno)
                  </small>
                </div>
              </div>
            ) : subscriptionInfo?.overdue ? (
              <div className="edit-user-sub-blocked">
                <p>
                  Tu suscripción <strong>venció</strong> y tienes <strong>48 horas</strong> para pagar. Si no
                  registras el pago, el bot se desactivará.
                </p>
                <div className="edit-user-sub-amount">
                  Monto a pagar: <b>{subDueAmount > 0 ? `${subDueAmount.toFixed(2)} USDT` : "—"}</b>
                </div>
              </div>
            ) : (
              <div className="edit-user-sub-ok">
                <div className="edit-user-sub-amount">
                  Monto a transferir al momento:{" "}
                  <b>{subDueAmount > 0 ? `${subDueAmount.toFixed(2)} USDT` : "0.00 USDT"}</b>{" "}
                  <small>
                    = {subscriptionInfo?.pct != null ? `${subscriptionInfo.pct}%` : "?"} de{" "}
                    <b>${Number(subscriptionInfo?.period_gains || 0).toFixed(2)}</b> ganados en el período
                    actual
                  </small>
                </div>
                {subscriptionInfo?.period_end ? (
                  <span>
                    Vencimiento del período: {formatSubDate(subscriptionInfo.period_end)}
                    {Number(subscriptionInfo?.days_left) <= 3 && Number(subscriptionInfo?.days_left) >= 0 ? (
                      <strong className="edit-user-sub-warn">
                        {" "}
                        · Quedan {subscriptionInfo.days_left} día(s). Para que el bot no se desactive, transfiere
                        {subDueAmount > 0 ? ` ${subDueAmount.toFixed(2)} USDT` : " el monto del pacto"} a la wallet{" "}
                        <code>{subWallet || "Pendiente"}</code> (TRC20) e ingresa el hash.
                      </strong>
                    ) : null}
                  </span>
                ) : null}
              </div>
            )}

            <div className="edit-user-sim-pay-zone">
              <div className="edit-user-sim-pay-zone-title">
                <span className="edit-user-sim-pay-zone-num">1</span>
                Cómo harás el pago
              </div>
              <div className="edit-user-sub-methods">
                <button
                  type="button"
                  className={`edit-user-sub-method${subPaymentMethod === "tron" ? " edit-user-sub-method--active" : ""}`}
                  onClick={() => setSubPaymentMethod("tron")}
                >
                  <span className="edit-user-sub-method-title">Externo · otra wallet</span>
                  <span className="edit-user-sub-method-desc">
                    Pagas con una wallet TRC20 y pegas el hash de la red.
                  </span>
                </button>
                <button
                  type="button"
                  className={`edit-user-sub-method${subPaymentMethod === "pionex" ? " edit-user-sub-method--active" : ""}`}
                  onClick={() => setSubPaymentMethod("pionex")}
                >
                  <span className="edit-user-sub-method-title">Pionex → Pionex · interno</span>
                  <span className="edit-user-sub-method-desc">
                    Pagas desde tu cuenta Pionex al UID del admin. Instantáneo, sin hash ni memo;
                    confirmas con el botón.
                  </span>
                </button>
              </div>
            </div>

            <div className="edit-user-sim-pay-zone">
              <div className="edit-user-sim-pay-zone-title">
                <span className="edit-user-sim-pay-zone-num">2</span>
                Pasos para pagar
              </div>

              {subPaymentMethod === "tron" ? (
              <div className="edit-user-sim-pay-steps">
                <p>
                  <strong>Paso 1:</strong> envía{" "}
                  <b>{subDueAmount > 0 ? `${subDueAmount.toFixed(2)} USDT` : "el monto del pacto"}</b>{" "}
                  (red TRC20) desde cualquier wallet a esta dirección:
                </p>
                <code>{subWallet || "Pendiente de definir"}</code>
                {subQR ? (
                  <div className="edit-user-activate-qr">
                    <img src={subQR} alt="QR pago suscripción" width="150" height="150" />
                    <small>O escanea el QR con tu wallet (TronLink, Trust Wallet, etc.)</small>
                  </div>
                ) : null}
                <p>
                  <strong>Paso 2:</strong> cuando el pago esté confirmado, copia el <strong>TX ID (hash)</strong>{" "}
                  del historial de la transacción (empieza con <code>T</code>) para pegarlo en la zona 3.
                </p>
              </div>
              ) : (
              <div className="edit-user-sim-pay-steps">
                <p>
                  <strong>Paso 1:</strong> dentro de tu cuenta Pionex abre{" "}
                  <b>Portafolio/Cartera → Transferir → Transferencia interna (Pionex a Pionex)</b> y envía{" "}
                  <b>{subDueAmount > 0 ? `${subDueAmount.toFixed(2)} USDT` : "el monto del pacto"}</b> al UID del
                  administrador:
                </p>
                <code className="edit-user-sim-pay-memo">{pionexUid || "UID_pendiente"}</code>
                {!pionexUid ? (
                  <p className="edit-user-sim-pay-warn">
                    El UID de la cuenta destino no está configurado aún. Escríbele al administrador antes de
                    transferir.
                  </p>
                ) : null}
                <p>
                  <strong>Paso 2:</strong> la transferencia llega <b>al instante</b>.{" "}
                  <b>No hay memo ni hash</b>.
                </p>
                <p>
                  <strong>Paso 3 (recomendado):</strong> anota la <b>fecha y hora exacta</b> de la
                  transferencia tal como la muestra Pionex (ej. <code>21/09/2026 13:27:07</code>); el
                  administrador ve la misma hora.
                </p>
                <p>
                  <strong>Paso 4:</strong> pulsa <b>"Ya transferí · Pagar"</b> en la zona 3. Tu pago quedará{" "}
                  <b>en revisión</b> del administrador.
                </p>
              </div>
              )}
            </div>

            <div className="edit-user-sim-pay-zone">
              <div className="edit-user-sim-pay-zone-title">
                <span className="edit-user-sim-pay-zone-num">3</span>
                Ingresa tu comprobante
              </div>

              {subPaymentMethod === "tron" ? (
              <div className="edit-user-activate-row">
                <input
                  type="text"
                  name="subscription_tx_hash"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  placeholder="Hash de la transacción (TX ID) — empieza con T"
                  value={subHash}
                  onChange={(event) => setSubHash(event.target.value)}
                  disabled={subLoading}
                />
                <button
                  type="button"
                  disabled={subLoading || !subHash.trim() || subDueAmount <= 0}
                  onClick={handleSubPay}
                >
                  {subLoading ? "Validando..." : "Pagar y validar"}
                </button>
              </div>
              ) : (
              <div className="edit-user-sim-pay-steps">
                <p>
                  Si tienes el historial abierto, pega aquí la <b>fecha y hora exacta</b> de la
                  transferencia tal como la muestra Pionex (ej.{" "}
                  <code>21/09/2026 13:27:07</code>). Ambos (tú y el administrador) ven la misma hora:
                  es tu comprobante.
                </p>
                <div className="edit-user-activate-row">
                  <input
                    type="text"
                    name="subscription_pionex_occurred_at"
                    autoComplete="off"
                    placeholder="Fecha y hora exacta (ej. 21/09/2026 13:27:07)"
                    value={subOccurredAt}
                    onChange={(event) => setSubOccurredAt(event.target.value)}
                    disabled={subLoading}
                  />
                  <button
                    type="button"
                    disabled={subLoading || subDueAmount <= 0}
                    onClick={handleSubPay}
                  >
                    {subLoading ? "Verificando..." : "Ya transferí · Pagar"}
                  </button>
                </div>
              </div>
              )}
              {subStatus.text ? (
                <span className={`edit-user-activate-status edit-user-activate-status--${subStatus.type}`}>
                  {subStatus.text}
                </span>
              ) : null}
            </div>

            {allowAdminControls && user?.subscription_payment_pending ? (
              <div className="edit-user-sub-admin-pending">
                <p>
                  <strong>Pago Pionex interno pendiente de verificar</strong>
                </p>
                <span>
                  Este usuario declaró que transfirió su pago de suscripción por transferencia interna Pionex a
                  tu UID. Verifica el ingreso en tu app Pionex antes de confirmar.
                </span>
                <div className="edit-user-sub-admin-pending-data">
                  <span>
                    Monto a confirmar:{" "}
                    {Number(user?.subscription_pending_amount || 0) > 0
                      ? `${Number(user.subscription_pending_amount).toFixed(2)} USDT`
                      : "—"}
                  </span>
                  {user?.subscription_payment_occurred_at ? (
                    <span>
                      Hora declarada de la transferencia: <code>{user.subscription_payment_occurred_at}</code>
                    </span>
                  ) : null}
                  {typeof user?.subscription_spot_delta === "number" ? (
                    <span
                      className={
                        user?.subscription_spot_possible
                          ? "edit-user-sub-admin-hint edit-user-sub-admin-hint--ok"
                          : "edit-user-sub-admin-hint"
                      }
                    >
                      {user?.subscription_spot_possible ? (
                        <>
                          Posible ingreso detectado: el saldo spot del admin subió <b>+${Number(user.subscription_spot_delta).toFixed(2)} USDT</b> desde el muestreo previo al aviso.
                        </>
                      ) : (
                        <>
                          Saldo spot del admin ahora: <b>{Number(user.subscription_spot_now || 0).toFixed(2)}</b>{" "}
                          USDT (delta desde el muestreo previo: +{Number(user.subscription_spot_delta).toFixed(2)}). El ingreso aún no parece reflejado.
                        </>
                      )}
                    </span>
                  ) : null}
                  <span className="edit-user-sub-admin-pending-help">
                    Revisa en tu app Pionex el historial de transferencias internas entrantes: busca una USDT por{" "}
                    <b>
                      {Number(user?.subscription_pending_amount || 0) > 0
                        ? `${Number(user.subscription_pending_amount).toFixed(2)} USDT`
                        : "el monto"}
                    </b>{" "}
                    {user?.subscription_payment_occurred_at ? (
                      <>a la hora <b>{user.subscription_payment_occurred_at}</b></>
                    ) : null}{" "}
                    (la hora exacta es idéntica en ambos lados). Si coincide, confirma; si no, rechaza.
                  </span>
                </div>
                <div className="edit-user-actions">
                  <button
                    type="button"
                    className="edit-user-primary"
                    disabled={subAdminBusy}
                    onClick={async () => {
                      setSubAdminBusy(true);
                      setSubAdminStatus({ type: "", text: "" });
                      const result = await confirmSubscriptionPayment(user.username, "confirm");
                      setSubAdminBusy(false);
                      if (result.success) {
                        setSubAdminStatus({ type: "success", text: result.data?.message || "Pago confirmado." });
                        onSubscriptionConfirmed?.();
                      } else {
                        setSubAdminStatus({ type: "error", text: result.error || "Error confirmando el pago." });
                      }
                    }}
                  >
                    {subAdminBusy ? "Procesando..." : "Sí, el ingreso llegó · Confirmar"}
                  </button>
                  <button
                    type="button"
                    className="edit-user-secondary"
                    disabled={subAdminBusy}
                    onClick={async () => {
                      setSubAdminBusy(true);
                      setSubAdminStatus({ type: "", text: "" });
                      const result = await confirmSubscriptionPayment(user.username, "reject");
                      setSubAdminBusy(false);
                      if (result.success) {
                        setSubAdminStatus({ type: "success", text: result.data?.message || "Pago rechazado." });
                        onSubscriptionConfirmed?.();
                      } else {
                        setSubAdminStatus({ type: "error", text: result.error || "Error rechazando el pago." });
                      }
                    }}
                  >
                    {subAdminBusy ? "Procesando..." : "No llegó · Rechazar"}
                  </button>
                </div>
                {subAdminStatus.text ? (
                  <span className={`edit-user-activate-status edit-user-activate-status--${subAdminStatus.type}`}>
                    {subAdminStatus.text}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

{showTradeFields && (
          <>
            <label className="edit-user-field">
              <span>Pares de operación</span>
            <TradingPairsSelector
              value={tradingPairs}
              onChange={setTradingPairs}
              symbolOptions={symbolOptions}
              symbolsLoading={symbolsLoading}
              symbolsError={symbolsError}
              onRefreshSymbols={() => loadSymbols(true)}
              disabled={loading}
            />
          </label>

          <div className="edit-user-assigned">
              <span className="edit-user-assigned-label">Vista asignada</span>
              <strong>Solo trade</strong>
            </div>

          {allowAdminControls ? (
            <label className="edit-user-field">
              <span>Modo</span>
              <select
                value={tradeMode}
                onChange={(event) => setTradeMode(event.target.value)}
                disabled={loading}
              >
                <option value="">Selecciona modo</option>
                <option value="SIM">Simulación</option>
                <option value="REAL">Producción</option>
              </select>
            </label>
          ) : (
            <div className="edit-user-assigned">
              <span className="edit-user-assigned-label">Modo asignado</span>
              <strong>{effectiveTradeMode === "REAL" ? "Producción" : "Simulación"}</strong>
            </div>
          )}

          <label className="edit-user-field">
            <span>Pionex API Key</span>
            <input
              type="text"
              autoComplete="off"
              placeholder="Dejar vacío para mantener"
              value={apiKey}
              onChange={(event) => {
                keysTouchedRef.current.key = true;
                setApiKey(event.target.value);
              }}
              disabled={loading}
            />
          </label>

          <label className="edit-user-field">
            <span>Pionex API Secret</span>
            <div className="edit-user-password">
              <input
                type={showSecret ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Dejar vacío para mantener"
                value={apiSecret}
                onChange={(event) => {
                  keysTouchedRef.current.secret = true;
                  setApiSecret(event.target.value);
                }}
                disabled={loading}
              />
              <button
                type="button"
                className="edit-user-toggle"
                onClick={() => setShowSecret((prev) => !prev)}
                disabled={loading}
              >
                {showSecret ? "Ocultar" : "Ver"}
              </button>
            </div>
          </label>

          <label className="edit-user-field">
            <span>Telegram Chat IDs</span>
            <input
              type="text"
              placeholder="Ej: 123456789, 987654321"
              value={telegramChatIds}
              onChange={(event) => setTelegramChatIds(event.target.value)}
              disabled={loading}
            />
          </label>

          <small className="edit-user-help">
            Escribe a <strong><a href="https://t.me/MultiTradeImBot" target="_blank" rel="noopener noreferrer">@MultiTradeImBot</a></strong> en Telegram y usa <strong>/start</strong> para obtener tu chat_id y vincular las notificaciones.
          </small>

          {allowAdminControls && effectiveTradeMode === "REAL" ? (
            <>
              <div className="edit-user-section-title">Suscripción (pacto por usuario)</div>
              <div className="edit-user-sub-admin">
                <label className="edit-user-field">
                  <span>% sobre ganancias del período</span>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="Ej: 20"
                    value={subscriptionPct}
                    onChange={(event) => setSubscriptionPct(event.target.value)}
                    disabled={loading}
                  />
                  <small className="edit-user-help-inline">Vacío = sin suscripción (% no pactado).</small>
                </label>
                <label className="edit-user-field">
                  <span>Período de facturación</span>
                  <select
                    value={subscriptionPeriodType || (subscriptionPeriodDays ? "no_periodo" : "")}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSubscriptionPeriodType(value);
                      if (value === "semanal") setSubscriptionPeriodDays("7");
                      else if (value === "quincenal") setSubscriptionPeriodDays("14");
                      else if (value === "mensual") setSubscriptionPeriodDays("30");
                      else if (value === "") setSubscriptionPeriodDays("");
                    }}
                    disabled={loading}
                  >
                    <option value="">Sin período definido</option>
                    <option value="semanal">Semanal</option>
                    <option value="quincenal">Quincenal (2 semanas)</option>
                    <option value="mensual">Mensual (mes calendario)</option>
                  </select>
                  <small className="edit-user-help-inline">
                    El vencimiento se calcula por calendario real: semanal = +7 días (mismo día), quincenal = +14 días (2 semanas, mismo día) y
                    mensual = misma fecha del mes siguiente (respeta meses de 28/29/30/31 días).
                  </small>
                </label>
                <label className="edit-user-field">
                  <span>Día de facturación (mensual)</span>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    placeholder="Ej: 10"
                    value={subscriptionBillingDay}
                    onChange={(event) => {
                      const raw = event.target.value;
                      if (raw === "") {
                        setSubscriptionBillingDay("");
                        return;
                      }
                      const n = Number(raw);
                      if (Number.isFinite(n)) setSubscriptionBillingDay(String(Math.min(Math.max(n, 1), 31)));
                    }}
                    disabled={loading}
                  />
                  <small className="edit-user-help-inline">
                    Día del mes en que se factura (el período cierra ese día). Aplica a períodos mensuales; vacío = se
                    usa la fecha de creación/pago.
                  </small>
                </label>
              </div>

              <div className="edit-user-sub-admin-paid">
                <small className="edit-user-help">
                  ¿Te pagaron por un medio físico (efectivo, banco, etc.)? Marca el período como pagado:
                  renueva el período, limpia pendientes y reactiva el bot.
                  El monto carga automáticamente (pacto % sobre ganancias del período); ajústalo si recibiste
                  una cantidad distinta.
                </small>
                <div className="edit-user-sub-admin-pending-help-actions">
                  <label className="edit-user-field">
                    <span>Monto recibido (opcional)</span>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      placeholder="Auto: monto calculado del período"
                      value={subPaidAmount}
                      onChange={(event) => setSubPaidAmount(event.target.value)}
                      disabled={subAdminBusy || loading}
                    />
                  </label>
                  <label className="edit-user-field">
                    <span>Referencia del pago (opcional)</span>
                    <input
                      type="text"
                      placeholder="Ej: efectivo / consignación B*"
                      value={subPaidRef}
                      onChange={(event) => setSubPaidRef(event.target.value)}
                      disabled={subAdminBusy || loading}
                    />
                  </label>
                </div>
                <div className="edit-user-actions">
                  <button
                    type="button"
                    className="edit-user-primary"
                    disabled={subAdminBusy || loading}
                    onClick={async () => {
                      setSubAdminBusy(true);
                      setSubAdminStatus({ type: "", text: "" });
                      const amount = subPaidAmount !== "" && subPaidAmount !== null ? Number(subPaidAmount) : null;
                      const result = await markSubscriptionPaid(user.username, {
                        amount: Number.isFinite(amount) && amount > 0 ? amount : null,
                        reference: subPaidRef || "",
                      });
                      setSubAdminBusy(false);
                      if (result.success) {
                        setSubAdminStatus({ type: "success", text: result.data?.message || "Período marcado como pagado." });
                        setSubPaidAmount("");
                        setSubPaidRef("");
                        onSubscriptionConfirmed?.();
                      } else {
                        setSubAdminStatus({ type: "error", text: result.error || "Error marcando el período como pagado." });
                      }
                    }}
                  >
                    {subAdminBusy ? "Procesando..." : "Marcar período pagado (medio físico)"}
                  </button>
                </div>
                {subAdminStatus.text ? (
                  <span className={`edit-user-activate-status edit-user-activate-status--${subAdminStatus.type}`}>
                    {subAdminStatus.text}
                  </span>
                ) : null}
              </div>
            </>
          ) : null}
          </>
        )}

          {allowPromptControl ? (
            <label className="edit-user-checkbox">
              <input
                type="checkbox"
                checked={hideProfilePrompt}
                onChange={(event) => setHideProfilePrompt(event.target.checked)}
                disabled={loading}
              />
              <span>No mostrar más este recordatorio al iniciar sesión</span>
            </label>
          ) : null}

          {allowPasswordChange ? (
            <>
              <div className="edit-user-section-title">Cambiar contraseña</div>
              <label className="edit-user-field">
                <span>Nueva contraseña</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={loading}
                  placeholder="Déjala vacía si no la vas a cambiar"
                />
              </label>
              <label className="edit-user-field">
                <span>Confirmar nueva contraseña</span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={loading}
                  placeholder="Repite la nueva contraseña"
                />
              </label>
            </>
          ) : null}

          <div className="edit-user-actions">
            {!forceComplete ? (
              <button
                type="button"
                className="edit-user-secondary"
                onClick={() => onClose?.()}
                disabled={loading}
              >
                Cancelar
              </button>
            ) : null}
            <button type="submit" className="edit-user-primary" disabled={loading}>
              {loading ? "Guardando..." : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const formatSubDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
};

export default EditUserModal;
